
import Dropdown from '@/components/UI/Dropdown.vue'
import ErrorMessage from '@/components/UI/ErrorMessage.vue'
import InputDropdown from '@/components/UI/InputDropdown.vue'
import SelectOption from '@/components/UI/SelectOption.vue'
import { CompanyService } from '@/services'
import { ActionType, EventType, PageName } from '@/types/track-events'
import arrowRight from '@iconify/icons-mdi/arrow-right'
import { Icon, addIcon } from '@iconify/vue'
import { defineComponent, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { logOnboardingGaEvent } from '../util/gaEvent'
import { trackCustomEvent, trackCustomFieldEvent } from '../util/trackEvent'

addIcon('arrow-right', arrowRight)

export default defineComponent({
  components: {
    Dropdown,
    SelectOption,
    Icon,
    ErrorMessage,
    InputDropdown
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const nicheOptions = ref([])
    console.log(store.state.business)
    const showNicheOptions = ref(store.state.business.showNicheOptions)
    // this is a shadow field to store the actual industry selected by the user
    const finalIndustrySelected = ref(store.state.business.industryServed)
    const selectedNiche = ref(store.state.business.niche)
    const selectedIndustry = ref(store.state.business.industryServed)
    const selectedPrimaryUse = ref(store.state.business.agencyPrimaryUseRaw)
    const selectCustomerCount = ref(store.state.business.customerCount)
    const website = ref(store.state.business.websiteUrl)
    const haveWebsite = ref(store.state.business.haveWebsite)
    const others = ref(store.state.business.agencyPrimaryUse)
    const showOthers =
      store.state.business.agencyPrimaryUseRaw === 'Others'
        ? ref(true)
        : ref(false)

    const formErrors = ref({
      industryError: false,
      primaryUseError: false,
      customerCountError: false,
      websiteError: false,
      othersError: false,
      nicheError: false
    })

    const sendCustomEvent = (val: string, fieldName: string) => {
      trackCustomFieldEvent({
        pageName: PageName.ONBOARDING_BUSINESS_INFO,
        elementName: fieldName,
        value: val,
        store
      })
    }

    watch(haveWebsite, () => {
      formErrors.value.websiteError = false
      website.value = ''
      sendCustomEvent(
        haveWebsite.value ? 'yes' : 'no',
        'onboarding_has_website'
      )
    })

    const verifyErrors = () => {
      if (!selectedIndustry.value) formErrors.value.industryError = true
      if (!selectedPrimaryUse.value) formErrors.value.primaryUseError = true
      if (!selectCustomerCount.value) formErrors.value.customerCountError = true
      if (haveWebsite.value && !website.value?.trim()) {
        website.value = website.value?.trim()
        formErrors.value.websiteError = true
      }
      if (showNicheOptions.value && !selectedNiche.value) {
        formErrors.value.nicheError = true
      }
      if (selectedPrimaryUse.value === 'Others' && !others.value?.trim()) {
        others.value = website.value?.trim()
        formErrors.value.othersError = true
      }
    }

    const industryOptions = [
      {
        value: 'Automotive',
        label: 'Automotive'
      },
      {
        value: 'Beauty & Fashion',
        label: 'Beauty & Fashion'
      },
      {
        value: 'Business Coaching and Consulting',
        label: 'Business Coaching and Consulting'
      },
      {
        value: 'Creative',
        label: 'Creative'
      },
      {
        value: 'Financial',
        label: 'Financial'
      },
      {
        value: 'Health & Wellness',
        label: 'Health & Wellness'
      },
      {
        value: 'Home Services',
        label: 'Home Services'
      },
      {
        value: 'Insurance',
        label: 'Insurance'
      },
      {
        value: 'Legal',
        label: 'Legal'
      },
      {
        value: 'Marketing Agency',
        label: 'Marketing Agency'
      },
      {
        value: 'Medical',
        label: 'Medical'
      },
      {
        value: 'Real Estate',
        label: 'Real Estate'
      },
      {
        value: 'Restaurant and Bar',
        label: 'Restaurant and Bar'
      },
      {
        value: 'Travel & Hospitality',
        label: 'Travel & Hospitality'
      },
      {
        value: 'Other',
        label: 'Other'
      }
    ]
    const primaryUseOptions = [
      {
        value: 'To operate my own business',
        label: 'To manage my own business'
      },
      {
        value: 'To help my clients or other businesses with marketing',
        label: "To manage my clients' businesses"
      },
      {
        value: 'For white-labelling and reselling software',
        label: 'White-labeling and reselling (Becoming a SaaSpreneur)'
      },
      { value: 'Others', label: 'Others' }
    ]
    const customerCountOptions = [
      '1-3',
      '4-9',
      '10-49',
      '50+',
      'I don’t have any customers yet'
    ]

    const updateIndustry = async (value: string) => {
      formErrors.value.industryError = false
      selectedIndustry.value = value
      selectedNiche.value = ''
      // we are using finalIndustrySelected because selectedIndustry will be 'Others' if the user selects 'Other' from the industry dropdown
      finalIndustrySelected.value = value
      try {
        if (finalIndustrySelected.value === 'Other') {
          if (nicheOptions.value.length === 0) {
            const niches = await CompanyService.getBusinessNiche(
              selectedIndustry.value
            )
            nicheOptions.value = niches.data
            showNicheOptions.value = true
          }
        } else {
          showNicheOptions.value = false
          nicheOptions.value = []
        }
      } catch (error) {
        console.error(error)
      }

      sendCustomEvent(value, 'onboarding_industry_served')
    }

    const updateNiche = (value: string, parent: string) => {
      formErrors.value.nicheError = false
      selectedNiche.value = value
      // map the parent industry to the finalIndustrySelected in case the user selects 'Other' from the industry dropdown
      finalIndustrySelected.value = parent

      sendCustomEvent(value, 'onboarding_industry_niche')
    }

    const updatePrimaryUse = (value: string) => {
      formErrors.value.primaryUseError = false
      selectedPrimaryUse.value = value

      if (value === 'Others') {
        others.value = ''
        showOthers.value = true
      } else {
        showOthers.value = false
      }

      sendCustomEvent(value, 'onboarding_primary_use')
    }

    const updateCustomerCount = (value: string) => {
      formErrors.value.customerCountError = false
      selectCustomerCount.value = value
      sendCustomEvent(value, 'onboarding_customer_count')
    }

    const nextStep = () => {
      verifyErrors()
      for (const error in formErrors.value) {
        if (formErrors.value[error]) return
      }
      store.commit('business/updateInfo', {
        // we are using finalIndustrySelected because selectedIndustry will be 'Others' if the user selects 'Other' from the industry dropdown
        // but we need the actual industry selected by the user
        industryServed: finalIndustrySelected.value,
        niche: selectedNiche.value,
        customerCount: selectCustomerCount.value,
        haveWebsite: haveWebsite.value,
        websiteUrl: website.value,
        agencyPrimaryUseRaw: selectedPrimaryUse.value,
        agencyPrimaryUse:
          selectedPrimaryUse.value === 'Others'
            ? others.value
            : selectedPrimaryUse.value
      })
      store.commit('business/set', {
        showNicheOptions: showNicheOptions.value
      })

      // @ts-ignore
      store?.state?.iframe?.handshake?.then(parent => {
        parent.emit('ga-event', 'onb-bizprofile')

        const gaData = {
          event: 'onboarding_business_info_complete',
          onboarding_step: '1'
        }
        logOnboardingGaEvent(gaData, store)
      })
      trackCustomEvent({
        eventType: EventType.PAGE_ACTION,
        actionType: ActionType.CLICK,
        pageName: PageName.ONBOARDING_BUSINESS_INFO,
        elementName: 'onboarding_info_complete',
        store
      })

      router.push({
        name: 'businessAddressPage'
      })
    }

    onMounted(() => {
      trackCustomEvent({
        eventType: EventType.PAGE_VISIT,
        actionType: ActionType.VIEW,
        pageName: PageName.ONBOARDING_BUSINESS_INFO,
        elementName: PageName.ONBOARDING_BUSINESS_INFO,
        store
      })
    })

    return {
      industryOptions,
      primaryUseOptions,
      customerCountOptions,
      selectedIndustry,
      selectedPrimaryUse,
      selectCustomerCount,
      haveWebsite,
      website,
      updateIndustry,
      updatePrimaryUse,
      updateCustomerCount,
      formErrors,
      nextStep,
      others,
      showOthers,
      sendCustomEvent,
      nicheOptions,
      selectedNiche,
      showNicheOptions,
      updateNiche
    }
  }
})
